// Mowkin-facing card shown on /dashboard. Lists households the user has
// served whose free cycle is ending, with an inline bid form for each.

import { useEffect, useState } from 'react';

interface Candidate {
  resident_id: number;
  resident_name: string;
  resident_address: string;
  resident_city: string;
  resident_county: string;
  yard_size: string | null;
  cycle_end: string | null;
  continuation_opt_in: string;
  mowkin_kind: 'volunteer' | 'provider';
  mowkin_id: number;
  existing_bid_status: string | null;
}

function fmtDate(iso: string | null) {
  return iso ? new Date(iso + 'T00:00:00').toLocaleDateString() : '—';
}

function BidForm({ c, onDone }: { c: Candidate; onDone: () => void }) {
  const [dollars, setDollars] = useState('60');
  const [cycleMonths, setCycleMonths] = useState(3);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const cents = Math.round(Number(dollars) * 100);
      if (!Number.isFinite(cents) || cents < 1000) throw new Error('Bid at least $10/month');
      const body: Record<string, unknown> = {
        resident_id: c.resident_id,
        bid_amount_cents: cents,
        cycle_months: cycleMonths,
        bid_notes: notes || undefined,
      };
      if (c.mowkin_kind === 'volunteer') body.volunteer_id = c.mowkin_id;
      else                                body.provider_id  = c.mowkin_id;
      const res = await fetch('/api/me/crowdfund-bid', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Bid failed');
      setMsg('Bid submitted. Admin will review.');
      onDone();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Bid failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-forest/10">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Per month (USD)</div>
          <div className="flex items-center gap-2">
            <span className="text-forest/55">$</span>
            <input
              type="number" min={10} step={5}
              value={dollars} onChange={(e) => setDollars(e.target.value)}
              className="flex-1 border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
            />
          </div>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Cycle length</div>
          <select
            value={cycleMonths}
            onChange={(e) => setCycleMonths(Number(e.target.value))}
            className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose"
          >
            <option value={1}>1 month</option>
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </select>
        </div>
      </div>
      <textarea
        rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes for admin (e.g. why this bid is fair)"
        className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-rose mb-3"
      />
      <button
        type="button" disabled={busy} onClick={submit}
        className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50"
      >
        {busy ? 'Sending...' : 'Submit bid for admin review'}
      </button>
      {msg && <span className="ml-3 text-sm text-forest/70">{msg}</span>}
    </div>
  );
}

export function BidInvitations() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await fetch('/api/me/bid-candidates', { credentials: 'include' });
      const j = await res.json() as { ok: boolean; data?: { candidates: Candidate[] } };
      if (j.ok) setCandidates(j.data?.candidates || []);
    } catch { /* swallow */ } finally { setLoaded(true); }
  };
  useEffect(() => { void load(); }, []);

  if (!loaded || candidates.length === 0) return null;

  return (
    <section className="mb-10">
      <h2 className="text-2xl text-forest font-display mb-2">Continue serving a household</h2>
      <p className="text-sm text-forest/65 mb-4 max-w-2xl">
        These households you served have cycles ending soon. If you'd like
        to keep mowing for them, propose a monthly price and we'll get it
        in front of their neighbors.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {candidates.map((c, idx) => (
          <div key={`${c.mowkin_kind}-${c.mowkin_id}-${c.resident_id}`} className="bg-cream border border-forest/15 p-4">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/70 px-2 py-0.5">
                {c.mowkin_kind}
              </span>
              <span className="text-xs text-forest/55">
                cycle ends {fmtDate(c.cycle_end)}
              </span>
              {c.continuation_opt_in === 'no' && (
                <span className="text-[10px] tracking-[0.15em] uppercase bg-sand text-forest px-2 py-0.5">
                  resident not yet opted in
                </span>
              )}
              {c.existing_bid_status && (
                <span className="text-[10px] tracking-[0.15em] uppercase bg-forest text-cream px-2 py-0.5">
                  bid: {c.existing_bid_status}
                </span>
              )}
            </div>
            <div className="font-medium text-forest">{c.resident_name}</div>
            <div className="text-sm text-forest/65 mb-2">{c.resident_address}, {c.resident_city}</div>
            {c.yard_size && (
              <div className="text-[10px] tracking-[0.15em] uppercase text-forest/60 mb-2">Yard: {c.yard_size}</div>
            )}
            {!c.existing_bid_status || ['denied', 'cancelled'].includes(c.existing_bid_status) ? (
              openIdx === idx ? (
                <BidForm c={c} onDone={() => { setOpenIdx(null); void load(); }} />
              ) : (
                <button
                  type="button"
                  onClick={() => setOpenIdx(idx)}
                  className="text-xs tracking-[0.15em] uppercase border border-rose text-rose px-3 py-1.5 hover:bg-rose hover:text-cream transition-colors"
                >
                  Bid to continue
                </button>
              )
            ) : (
              <p className="text-xs text-forest/55 italic">
                You've already bid for this household. Admin is reviewing.
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
